import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Trophy, RefreshCw, Target, Award, Brain, CheckCircle, XCircle, Stethoscope, Activity, BookOpen, Heart, Circle, Move, Focus, Check, User, Filter, LogOut, Image, Mic, Crown } from 'lucide-react';
import { getRandomQuestion, getQuestionsByRegions, regions, DIFFICULTY_LEVELS, QUESTION_TYPES, shuffleQuestions } from './data/questionsData';
import { difficultyConfig, questionTypeConfig } from './data/questionTypes';
import ExplanationCard from './components/ExplanationCard';
import { questionExplanations } from './data/questionExplanations';
import ProgressModal from './components/ProgressModal';
import AchievementNotification from './components/AchievementNotification';
import AdvancedFilters from './components/AdvancedFilters';
import StartScreen from './components/StartScreen';
import QuizSetup from './components/QuizSetup';
import ExamSetup from './components/ExamSetup';
import ProfileSetup from './components/ProfileSetup';
import Lobby from './components/Lobby';
import SaveIndicator from './components/SaveIndicator';
import DebugPanel from './components/DebugPanel';
import { useAuth } from './contexts/AuthContext';
import { playCorrectSound, playIncorrectSound, playClickSound } from './utils/sounds';
import { saveQuizHistory, saveAchievement, getQuizHistory, saveRegionHistory, getRegionHistory, getUserAchievements, getGlobalQuizFeed, getRanking, createSocialPost, getSocialPosts, uploadSocialMedia, togglePostHeart, updateSocialPost, deleteSocialPost } from './utils/supabase';

// Importar directamente ProfileSetup para evitar problemas de inicialización en producción

function App() {
  // Game phases: 'start' | 'profile-setup' | 'lobby' | 'setup' | 'playing' | 'exam-setup' | 'exam-playing' | 'social'
  const [gamePhase, setGamePhase] = useState('start');
  const { isAuthenticated, user, profile, needsProfileSetup, updateUserProfile } = useAuth();
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [newAchievement, setNewAchievement] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [incorrectAnswers, setIncorrectAnswers] = useState(0);
  const [regionStats, setRegionStats] = useState({});
  const [askedQuestions, setAskedQuestions] = useState([]);
  const [unlockedAchievements, setUnlockedAchievements] = useState(new Set());

  const [examQuestions, setExamQuestions] = useState([]);
  const [examCurrentIndex, setExamCurrentIndex] = useState(0);
  const [examCorrectCount, setExamCorrectCount] = useState(0);
  const [examFinished, setExamFinished] = useState(false);
  const [examConfig, setExamConfig] = useState(null);
  const [examTotalTime, setExamTotalTime] = useState(0); // en segundos
  const [examTimeLeft, setExamTimeLeft] = useState(0);   // en segundos
  const [examCountdown, setExamCountdown] = useState(0);  // 3-2-1 antes de empezar
  const [examResultPhase, setExamResultPhase] = useState('playing'); // playing | evaluating | results
  const [examRegionStats, setExamRegionStats] = useState({});
  const [examEvaluationStep, setExamEvaluationStep] = useState(0);
  const [examAnswers, setExamAnswers] = useState([]);
  const [examSummaryPosted, setExamSummaryPosted] = useState(false);

  // Add exit confirmation modal state
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Modal para elegir modo de juego (Quiz / Examen)
  const [showPlayModal, setShowPlayModal] = useState(false);

  // Save status indicator
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [saveMessage, setSaveMessage] = useState('');

  // Accumulated stats from database
  const [accumulatedStats, setAccumulatedStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [accumulatedAchievements, setAccumulatedAchievements] = useState(null);

  // Social feed state
  const [socialPosts, setSocialPosts] = useState([]);
  const [isLoadingSocial, setIsLoadingSocial] = useState(false);
  const [socialError, setSocialError] = useState('');
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostBody, setNewPostBody] = useState('');
  const [newPostImageFile, setNewPostImageFile] = useState(null);
  const [newPostAudioFile, setNewPostAudioFile] = useState(null);
  const [expandedSystemPostId, setExpandedSystemPostId] = useState(null);
  const [hasPendingSocialTutorial, setHasPendingSocialTutorial] = useState(false);
  const [showSocialTutorial, setShowSocialTutorial] = useState(false);
  const [socialTutorialStep, setSocialTutorialStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [tutorialTooltipPlacement, setTutorialTooltipPlacement] = useState('bottom'); // 'top' | 'bottom'

  const bottomNavbarRef = useRef(null);
  const socialContainerRef = useRef(null);
  const socialFormRef = useRef(null);
  const socialActivityRef = useRef(null);

  // Rango del usuario en el ranking (basado en ranking_view)
  const [userRankLabel, setUserRankLabel] = useState(null);

  // Lobby refresh key to force re-mount and ranking reload
  const [lobbyKey, setLobbyKey] = useState(0);

  // Version panel state
  const [appVersion, setAppVersion] = useState(null);
  const [appChanges, setAppChanges] = useState([]);
  const [showVersionPanel, setShowVersionPanel] = useState(false);

  // Colores posibles para tags de posts
  const TAG_COLOR_CLASSES = [
    'bg-purple-50 text-purple-700 border-purple-200',
    'bg-pink-50 text-pink-700 border-pink-200',
    'bg-blue-50 text-blue-700 border-blue-200',
    'bg-emerald-50 text-emerald-700 border-emerald-200',
    'bg-amber-50 text-amber-700 border-amber-200',
  ];

  const getTagColorClass = (tag) => {
    if (!tag) return TAG_COLOR_CLASSES[0];
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = (hash * 31 + tag.charCodeAt(i)) | 0;
    }
    const idx = Math.abs(hash) % TAG_COLOR_CLASSES.length;
    return TAG_COLOR_CLASSES[idx];
  };

  const STRONG_WORDS = ['puta', 'puto', 'putas', 'putos', 'mierda', 'coño', 'pendejo', 'pendeja'];

  const sanitizeContent = (text) => {
    if (!text) return '';
    let result = text;
    STRONG_WORDS.forEach((word) => {
      const pattern = new RegExp(`\\b${word}\\b`, 'gi');
      result = result.replace(pattern, (match) => {
        if (match.length <= 2) return '*'.repeat(match.length);
        return match[0] + '*'.repeat(match.length - 1);
      });
    });
    return result;
  };

  const buildQuizSummaryPostContent = (quizData) => {
    const regionsList = (quizData.regions || []).map(getRegionDisplayName).join(', ') || 'Sin regiones registradas';
    const difficultiesList = (quizData.difficulties || []).join(', ') || 'Sin dificultad asignada';
    const accuracyText = typeof quizData.accuracy === 'number' ? `${quizData.accuracy}%` : `${accuracy}%`;

    return [
      '📜 Resultado del quiz de anatomía',
      '',
      `Puntaje obtenido: ${quizData.score} puntos`,
      `Preguntas respondidas: ${quizData.attempts}`,
      `Precisión: ${accuracyText}`,
      '',
      `Regiones estudiadas: ${regionsList}`,
      `Niveles de dificultad: ${difficultiesList}`,
      '',
      '#quiz #anatomia #progreso',
    ].join('\n');
  };

  const buildExamSummaryPostContent = () => {
    const totalQuestionsExam = examConfig?.total || examQuestions.length || 1;
    const rawGrade = (examCorrectCount / totalQuestionsExam) * 10;
    const grade = Number.isFinite(rawGrade) ? rawGrade.toFixed(2) : '0.00';
    const accuracyPctExam = Math.round((examCorrectCount / totalQuestionsExam) * 100);

    const displayName = profile?.username || profile?.display_name || user?.email || 'Estudiante';
    const rankText = userRankLabel ? ` (${userRankLabel})` : '';

    const regionsSummary = Object.entries(examRegionStats || {})
      .map(([regionId, stats]) => {
        const regionName = getRegionDisplayName(regionId);
        const total = stats.total || 0;
        const correct = stats.correct || 0;
        const acc = total > 0 ? Math.round((correct / total) * 100) : 0;
        return `${regionName}: ${acc}% (${correct}/${total})`;
      })
      .join(' · ') || 'Sin datos por regiones';

    const answersSummaryLines = (examAnswers || [])
      .slice(0, 10)
      .map((a, index) => {
        const selected = a.options[a.selectedAnswerIndex];
        const correct = a.options[a.correctAnswerIndex];
        const status = a.isCorrect ? '✔️' : '❌';
        return `${status} Pregunta ${index + 1}: "${a.question}" · Tu respuesta: ${selected}${a.isCorrect ? '' : ` · Correcta: ${correct}`}`;
      });

    const moreAnswersNote = examAnswers.length > 10
      ? `(+${examAnswers.length - 10} preguntas adicionales)`
      : '';

    const headerLines = [
      '__SYSTEM_EXAM_CERT__',
      '🏅 Certificado de Evaluación de Anatomía',
      '',
      `Quiz-Anatomia informa: ${displayName}${rankText} ha obtenido una nota de ${grade} / 10 en el Examen de Evaluación.`,
      '',
      `Preguntas correctas: ${examCorrectCount} de ${totalQuestionsExam} (${accuracyPctExam}%)`,
      `Regiones evaluadas: ${regionsSummary}`,
    ];

    const detailLines = [
      'Detalle del certificado:',
      ...answersSummaryLines,
      moreAnswersNote,
      '',
      '#examen #anatomia #certificado',
    ].filter(Boolean);

    return [
      ...headerLines,
      '',
      '---DETALLE---',
      '',
      ...detailLines,
    ].join('\n');
  };

  const publishQuizSummaryIfPossible = async (quizData) => {
    if (!isAuthenticated || !user) return;
    try {
      const content = sanitizeContent(buildQuizSummaryPostContent(quizData));
      const { error } = await createSocialPost(user.id, {
        content,
        imageUrls: [],
        audioUrls: [],
      });

      if (!error) {
        const { data: refreshed, error: refreshError } = await getSocialPosts(30);
        if (!refreshError && refreshed) {
          setSocialPosts(refreshed);
        }
      }
    } catch (e) {
      console.error('Error creando post automático de quiz:', e);
    }
  };

  const publishExamSummaryIfPossible = async () => {
    if (!isAuthenticated || !user) return;
    try {
      const content = sanitizeContent(buildExamSummaryPostContent());
      const { error } = await createSocialPost(user.id, {
        content,
        imageUrls: [],
        audioUrls: [],
      });

      if (!error) {
        const { data: refreshed, error: refreshError } = await getSocialPosts(30);
        if (!refreshError && refreshed) {
          setSocialPosts(refreshed);
        }
      }
    } catch (e) {
      console.error('Error creando post automático de examen:', e);
    }
  };

  // Cuando el usuario se autentica, decidir a dónde ir según si necesita completar perfil
  useEffect(() => {
    if (isAuthenticated && gamePhase === 'start') {
      setGamePhase(needsProfileSetup ? 'profile-setup' : 'lobby');
    }
  }, [isAuthenticated, needsProfileSetup, gamePhase]);

  const handleEditPost = async (post) => {
    if (!isAuthenticated || !user) {
      alert('Debes iniciar sesión para editar publicaciones.');
      return;
    }
    const isOwnPost = post.user_id === user.id;
    if (!isOwnPost && !profile?.is_admin) {
      alert('Solo el autor o un administrador pueden editar esta publicación.');
      return;
    }

    const currentContent = post.content || '';
    const edited = window.prompt('Editar contenido del post:', currentContent);
    if (edited == null || edited === currentContent) return;

    const sanitized = sanitizeContent(edited);

    const { data, error } = await updateSocialPost(post.id, { content: sanitized });
    if (error) {
      console.error('Error actualizando post social:', error);
      alert('No se pudo actualizar la publicación.');
      return;
    }

    setSocialPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, content: data.content } : p)));
  };

  const handleDeletePost = async (post) => {
    if (!isAuthenticated || !user) {
      alert('Debes iniciar sesión para eliminar publicaciones.');
      return;
    }
    const isOwnPost = post.user_id === user.id;
    if (!isOwnPost && !profile?.is_admin) {
      alert('Solo el autor o un administrador pueden eliminar esta publicación.');
      return;
    }

    const confirmed = window.confirm('¿Seguro que quieres eliminar esta publicación? Esta acción no se puede deshacer.');
    if (!confirmed) return;

    const { error } = await deleteSocialPost(post.id);
    if (error) {
      console.error('Error eliminando post social:', error);
      alert('No se pudo eliminar la publicación.');
      return;
    }

    setSocialPosts((prev) => prev.filter((p) => p.id !== post.id));
  };

  // Cargar feed social cuando entramos a la vista Social
  useEffect(() => {
    const loadSocial = async () => {
      setIsLoadingSocial(true);
      setSocialError('');
      try {
        const { data, error } = await getSocialPosts(30);
        if (error) {
          console.error('Error cargando feed social:', error);
          setSocialError('No se pudo cargar el feed social.');
        } else {
          setSocialPosts(data || []);
        }
      } catch (err) {
        console.error('Error de red al cargar feed social:', err);
        setSocialError('Error de red al cargar el feed social.');
      } finally {
        setIsLoadingSocial(false);
      }
    };

    if (gamePhase === 'social') {
      loadSocial();
    }
  }, [gamePhase]);

  // Calcular rango del usuario usando la vista ranking_view
  useEffect(() => {
    const loadUserRank = async () => {
      if (!isAuthenticated || !user) {
        setUserRankLabel(null);
        return;
      }

      try {
        const { data, error } = await getRanking(20);
        if (error || !data) {
          console.error('Error cargando ranking para rango de usuario:', error);
          setUserRankLabel(null);
          return;
        }

        const index = data.findIndex((row) => row.id === user.id);
        if (index === -1) {
          setUserRankLabel(null);
          return;
        }

        const position = index + 1; // posiciones empiezan en 1
        if (position === 1) {
          setUserRankLabel('Top 1');
        } else if (position <= 3) {
          setUserRankLabel('Top 3');
        } else if (position <= 10) {
          setUserRankLabel('Top 10');
        } else {
          setUserRankLabel(null);
        }
      } catch (err) {
        console.error('Error de red cargando ranking para rango de usuario:', err);
        setUserRankLabel(null);
      }
    };

    loadUserRank();
  }, [isAuthenticated, user]);

  // Usuarios distintos que han publicado en Social (para mostrar chips superiores)
  const otherSocialUsers = useMemo(() => {
    const myId = user?.id;
    const ids = new Set();
    (socialPosts || []).forEach((p) => {
      if (p.user_id && p.user_id !== myId) {
        ids.add(p.user_id);
      }
    });
    return Array.from(ids);
  }, [socialPosts, user]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!isAuthenticated || !user) {
      alert('Debes iniciar sesión para publicar.');
      return;
    }
    if (!newPostTitle && !newPostBody && !newPostImageFile && !newPostAudioFile) {
      alert('Escribe un mensaje o añade una imagen/audio para publicar.');
      return;
    }

    let imageUrls = [];
    let audioUrls = [];

    try {
      if (newPostImageFile) {
        const { url, error } = await uploadSocialMedia(newPostImageFile, 'image');
        if (error || !url) {
          alert('No se pudo subir la imagen.');
          return;
        }
        imageUrls.push(url);
      }

      if (newPostAudioFile) {
        const { url, error } = await uploadSocialMedia(newPostAudioFile, 'audio');
        if (error || !url) {
          alert('No se pudo subir el audio.');
          return;
        }
        audioUrls.push(url);
      }

      const sanitizedTitle = sanitizeContent(newPostTitle.trim());
      const sanitizedBody = sanitizeContent(newPostBody.trim());
      const combinedContent = sanitizedTitle && sanitizedBody
        ? `${sanitizedTitle}\n\n${sanitizedBody}`
        : (sanitizedTitle || sanitizedBody);

      const { error } = await createSocialPost(user.id, {
        content: combinedContent,
        imageUrls,
        audioUrls,
      });

      if (error) {
        console.error('Error creando post social:', error);
        alert('No se pudo publicar. Intenta de nuevo.');
        return;
      }

      // Tras publicar, recargar el feed completo para asegurar que aparecen todos los posts (incluyendo solo texto)
      const { data: refreshed, error: refreshError } = await getSocialPosts(30);
      if (!refreshError && refreshed) {
        setSocialPosts(refreshed);
      }

      setNewPostTitle('');
      setNewPostBody('');
      setNewPostImageFile(null);
      setNewPostAudioFile(null);
    } catch (err) {
      console.error('Error general creando post social:', err);
      alert('Ocurrió un error al publicar. Intenta de nuevo más tarde.');
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      Brain,
      Stethoscope,
      Heart,
      Circle,
      Move,
      Focus,
      Activity,
      BookOpen
    };
    return icons[iconName] || Brain;
  };

  const handleExamSetup = (config) => {
    const regionsForExam = config.regions || [];
    const difficultyForExam = config.difficulty;

    const allExamCandidates = getQuestionsByRegions(regionsForExam, [difficultyForExam], []);

    if (!allExamCandidates || allExamCandidates.length === 0) {
      alert('No se encontraron preguntas para este examen con la configuración seleccionada. Prueba con otras regiones o nivel.');
      return;
    }

    const shuffled = shuffleQuestions(allExamCandidates);
    const maxQuestions = Math.min(50, shuffled.length);
    const selectedExamQuestions = shuffled.slice(0, maxQuestions);

    setExamQuestions(selectedExamQuestions);
    setExamCurrentIndex(0);
    setExamCorrectCount(0);
    setExamFinished(false);
    setExamSummaryPosted(false);
    setExamAnswers([]);
    setExamConfig({ regions: regionsForExam, difficulty: difficultyForExam, total: maxQuestions });
    setSelectedRegions(regionsForExam);
    setSelectedDifficulties([difficultyForExam]);
    setSelectedTypes(Object.values(QUESTION_TYPES));
    // Configurar tiempo total según nivel
    let totalSeconds = 0;
    if (difficultyForExam === DIFFICULTY_LEVELS.BASICO) {
      totalSeconds = 10 * 60; // 10 minutos
    } else if (difficultyForExam === DIFFICULTY_LEVELS.GENERAL) {
      totalSeconds = 5 * 60; // 5 minutos
    } else if (difficultyForExam === DIFFICULTY_LEVELS.COMPLEJO) {
      totalSeconds = 3 * 60; // 3 minutos
    }
    setExamTotalTime(totalSeconds);
    setExamTimeLeft(totalSeconds);
    // Preparar cuenta regresiva antes de comenzar
    setExamCountdown(3);
    setGamePhase('exam-playing');
    // Aún no se muestra la primera pregunta hasta que termine la cuenta regresiva
    setCurrentQuestion(null);
    setAskedQuestions([]);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
    setGameStarted(false);
  };

  const handleExamAnswerClick = useCallback((answerIndex) => {
    if (!currentQuestion || gamePhase !== 'exam-playing' || examFinished) return;

    const correct = answerIndex === currentQuestion.correctAnswer;
    if (correct) {
      setExamCorrectCount(prev => prev + 1);
    }

    setExamAnswers(prev => [
      ...prev,
      {
        questionId: currentQuestion.id,
        question: currentQuestion.question,
        options: currentQuestion.options,
        correctAnswerIndex: currentQuestion.correctAnswer,
        selectedAnswerIndex: answerIndex,
        isCorrect: correct,
        region: currentQuestion.region,
        type: currentQuestion.type,
        explanation: currentQuestion.explanation
      }
    ]);

    const region = currentQuestion.region;
    setExamRegionStats(prev => {
      const prevRegion = prev[region] || { correct: 0, incorrect: 0, total: 0 };
      return {
        ...prev,
        [region]: {
          correct: prevRegion.correct + (correct ? 1 : 0),
          incorrect: prevRegion.incorrect + (correct ? 0 : 1),
          total: prevRegion.total + 1
        }
      };
    });

    // Avanzar inmediatamente a la siguiente pregunta sin feedback visual/sonoro
    const nextIndex = examCurrentIndex + 1;

    if (nextIndex >= examQuestions.length) {
      setExamFinished(true);
      setExamResultPhase('evaluating');
      setExamEvaluationStep(0);
      setCurrentQuestion(null);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setIsCorrect(false);
      return;
    }

    const nextQuestion = examQuestions[nextIndex];
    setExamCurrentIndex(nextIndex);
    setCurrentQuestion(nextQuestion);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
  }, [currentQuestion, gamePhase, examFinished, examCurrentIndex, examQuestions]);

  const goToNextExamQuestion = () => {
    if (!examQuestions.length || examFinished) return;

    const nextIndex = examCurrentIndex + 1;

    if (nextIndex >= examQuestions.length) {
      setExamFinished(true);
      setCurrentQuestion(null);
      setShowFeedback(false);
      setSelectedAnswer(null);
      return;
    }

    const nextQuestion = examQuestions[nextIndex];
    setExamCurrentIndex(nextIndex);
    setCurrentQuestion(nextQuestion);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
  };

  // Cuenta regresiva 3-2-1 antes de mostrar la primera pregunta del examen
  useEffect(() => {
    if (gamePhase !== 'exam-playing') return;
    if (examFinished) return;
    if (examCountdown <= 0) return;

    const interval = setInterval(() => {
      setExamCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // Iniciar examen mostrando primera pregunta y arrancar el tiempo
          if (examQuestions.length > 0) {
            setCurrentQuestion(examQuestions[0]);
            setExamCurrentIndex(0);
            setSelectedAnswer(null);
            setShowFeedback(false);
            setIsCorrect(false);
            setGameStarted(true);
            setExamTimeLeft(examTotalTime);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [gamePhase, examCountdown, examFinished, examQuestions, examTotalTime]);

  // Temporizador general del examen
  useEffect(() => {
    if (gamePhase !== 'exam-playing') return;
    if (examFinished) return;
    if (examCountdown > 0) return; // esperar a que termine la cuenta regresiva inicial
    if (examTimeLeft <= 0) {
      setExamFinished(true);
      setExamResultPhase('evaluating');
      setExamEvaluationStep(0);
      setCurrentQuestion(null);
      return;
    }

    const interval = setInterval(() => {
      setExamTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setExamFinished(true);
          setExamResultPhase('evaluating');
          setExamEvaluationStep(0);
          setCurrentQuestion(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [gamePhase, examFinished, examCountdown, examTimeLeft]);

  // Fase de evaluación del examen (pantalla intermedia animada)
  useEffect(() => {
    if (!examFinished) return;
    if (examResultPhase !== 'evaluating') return;

    const steps = [0, 1, 2];
    let current = 0;

    setExamEvaluationStep(0);

    const interval = setInterval(() => {
      current += 1;
      if (current >= steps.length) {
        clearInterval(interval);
        setExamResultPhase('results');
        return;
      }
      setExamEvaluationStep(current);
    }, 1500);

    return () => clearInterval(interval);
  }, [examFinished, examResultPhase]);

  useEffect(() => {
    if (!examFinished) return;
    if (examResultPhase !== 'results') return;
    if (examSummaryPosted) return;

    setExamSummaryPosted(true);
    publishExamSummaryIfPossible();
  }, [examFinished, examResultPhase, examSummaryPosted, examAnswers, examRegionStats, examCorrectCount, examConfig]);

  const startNewRound = () => {
    console.log('startNewRound called, selectedRegions:', selectedRegions, 'gamePhase:', gamePhase);
    
    // If we're in playing phase but no regions are selected, go back to setup
    if (gamePhase === 'playing' && selectedRegions.length === 0) {
      console.warn('No regions selected in playing phase, redirecting to setup');
      setGamePhase('setup');
      alert('Parece que no hay configuración. Te llevamos al setup para configurar el quiz.');
      return;
    }

    if (selectedRegions.length === 0) {
      alert('Error: No hay regiones seleccionadas. Regresa al setup para configurar el quiz.');
      return;
    }

    if (selectedDifficulties.length === 0) {
      alert('Error: No hay dificultades seleccionadas. Regresa al setup para configurar el quiz.');
      return;
    }

    if (selectedTypes.length === 0) {
      alert('Error: No hay tipos de pregunta seleccionados. Regresa al setup para configurar el quiz.');
      return;
    }

    playClickSound();

    const question = getRandomQuestion(selectedRegions, askedQuestions, selectedDifficulties, selectedTypes);
    
    if (!question) {
      setAskedQuestions([]);
      const newQuestion = getRandomQuestion(selectedRegions, [], selectedDifficulties, selectedTypes);
      setCurrentQuestion(newQuestion);
      if (newQuestion) setAskedQuestions([newQuestion.id]);
    } else {
      setCurrentQuestion(question);
      setAskedQuestions([...askedQuestions, question.id]);
    }
    
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
    if (!gameStarted) setGameStarted(true);
  };

  const handleAnswerClick = useCallback((answerIndex) => {
    if (showFeedback || !currentQuestion) return;

    setSelectedAnswer(answerIndex);
    setAttempts(prev => prev + 1);
    
    const correct = answerIndex === currentQuestion.correctAnswer;
    setIsCorrect(correct);
    setShowFeedback(true);

    // Play sound
    if (correct) {
      playCorrectSound();
    } else {
      playIncorrectSound();
    }

    // Update region stats
    const region = currentQuestion.region;
    setRegionStats(prev => ({
      ...prev,
      [region]: {
        correct: (prev[region]?.correct || 0) + (correct ? 1 : 0),
        incorrect: (prev[region]?.incorrect || 0) + (correct ? 0 : 1),
        total: (prev[region]?.total || 0) + 1
      }
    }));

    if (correct) {
      setScore(prev => prev + 10);
      setCorrectAnswers(prev => prev + 1);
      setStreak(prev => {
        const newStreak = prev + 1;
        setBestStreak(current => Math.max(current, newStreak));
        return newStreak;
      });
    } else {
      setIncorrectAnswers(prev => prev + 1);
      setStreak(0);
    }
  }, [showFeedback, currentQuestion]);

  const saveQuizProgress = useCallback(async (reason = 'manual') => {
    console.log(`[saveQuizProgress] Razón: ${reason}`, {
      isAuthenticated,
      hasUser: !!user,
      attempts,
      score,
      selectedRegions,
      selectedDifficulties,
      selectedTypes
    });

    if (!isAuthenticated || !user || attempts <= 0) {
      return;
    }

    const computedAccuracy = attempts > 0 ? Math.round((score / (attempts * 10)) * 100) : 0;
    const quizData = {
      score,
      attempts,
      accuracy: computedAccuracy,
      regions: selectedRegions,
      difficulties: selectedDifficulties,
      questionTypes: selectedTypes
    };

    try {
      setSaveStatus('saving');
      const [historyResult, regionResult] = await Promise.all([
        saveQuizHistory(user.id, quizData),
        saveRegionHistory(user.id, regionStats)
      ]);

      if (historyResult.error || regionResult.error) {
        const error = historyResult.error || regionResult.error;
        console.error('[saveQuizProgress] Error guardando historial o regiones:', error);
        setSaveStatus('error');
        setSaveMessage(error.message || 'Error desconocido');
        alert('Error al guardar tu progreso: ' + (error.message || 'Error desconocido'));
        setTimeout(() => setSaveStatus('idle'), 3000);
      } else {
        console.log('[saveQuizProgress] ✅ Historial y regiones guardados correctamente');
        if (reason !== 'autosave') {
          publishQuizSummaryIfPossible(quizData);
        }
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      }
    } catch (error) {
      console.error('[saveQuizProgress] Error guardando historial:', error);
      setSaveStatus('error');
      setSaveMessage('Error de red');
      alert('Error al guardar tu progreso');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [isAuthenticated, user, attempts, score, selectedRegions, selectedDifficulties, selectedTypes, regionStats]);

  useEffect(() => {
    if (attempts > 0 && attempts % 5 === 0 && gamePhase === 'playing') {
      saveQuizProgress('autosave');
    }
  }, [attempts, gamePhase, saveQuizProgress]);

  const resetGame = useCallback(async () => {
    // Save progress before resetting if there's any
    console.log('[resetGame] Intentando guardar progreso', {
      isAuthenticated,
      hasUser: !!user,
      attempts,
      score,
      selectedRegions,
      selectedDifficulties,
      selectedTypes
    });

    if (isAuthenticated && user && attempts > 0) {
      const computedAccuracy = attempts > 0 ? Math.round((score / (attempts * 10)) * 100) : 0;
      const quizData = {
        score,
        attempts,
        accuracy: computedAccuracy,
        regions: selectedRegions,
        difficulties: selectedDifficulties,
        questionTypes: selectedTypes
      };

      try {
        setSaveStatus('saving');
        const [historyResult, regionResult] = await Promise.all([
          saveQuizHistory(user.id, quizData),
          saveRegionHistory(user.id, regionStats)
        ]);

        if (historyResult.error || regionResult.error) {
          const error = historyResult.error || regionResult.error;
          console.error('Error guardando historial o regiones:', error);
          setSaveStatus('error');
          setSaveMessage(error.message || 'Error desconocido');
          alert('Error al guardar tu progreso: ' + (error.message || 'Error desconocido'));
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          console.log('[resetGame] ✅ Historial y regiones guardados correctamente');
          publishQuizSummaryIfPossible(quizData);
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        }
      } catch (error) {
        console.error('Error guardando historial:', error);
        setSaveStatus('error');
        setSaveMessage('Error de red');
        alert('Error al guardar tu progreso');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    }

    setScore(0);
    setAttempts(0);
    setStreak(0);
    setBestStreak(0);
    setCorrectAnswers(0);
    setIncorrectAnswers(0);
    setRegionStats({});
    setAskedQuestions([]);
    setGameStarted(false);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setUnlockedAchievements(new Set());
    setNewAchievement(null);
    setLobbyKey(prev => prev + 1); // Refresh lobby when resetting
    setGamePhase('start');
  }, [saveQuizProgress]);

  const handleStartClick = () => {
    // Si está autenticado, ir a profile-setup si falta completar; sino ir al lobby
    if (isAuthenticated) {
      if (needsProfileSetup) {
        setGamePhase('profile-setup');
      } else {
        setGamePhase('lobby');
      }
    } else {
      // No autenticado: StartScreen mostrará modal de login y llamará onLoginSuccess
      setGamePhase('start');
    }
  };

  const handleProfileComplete = async (profileData) => {
    const { error } = await updateUserProfile(profileData);
    if (!error) {
      // Después de completar el perfil, ir al Lobby
      setGamePhase('lobby');
    } else {
      console.error('Error updating profile:', error);
      alert('Error al guardar el perfil. Por favor intenta de nuevo.');
    }
  };

  const handleQuizSetup = (config) => {
    console.log('handleQuizSetup called with config:', config);
    setSelectedRegions(config.regions);
    setSelectedDifficulties(config.difficulties);
    setSelectedTypes(config.types);
    console.log('selectedRegions set to:', config.regions);
    setGamePhase('playing');
    // Start first question with config values directly
    startNewRoundWithConfig(config);
  };

  const startNewRoundWithConfig = (config) => {
    const regions = config.regions || selectedRegions;
    const difficulties = config.difficulties || selectedDifficulties;
    const types = config.types || selectedTypes;

    console.log('startNewRoundWithConfig called with regions:', regions);

    if (regions.length === 0) {
      alert('Error: No hay regiones seleccionadas. Regresa al setup para configurar el quiz.');
      setGamePhase('setup');
      return;
    }

    if (difficulties.length === 0) {
      alert('Error: No hay dificultades seleccionadas. Regresa al setup para configurar el quiz.');
      setGamePhase('setup');
      return;
    }

    if (types.length === 0) {
      alert('Error: No hay tipos de pregunta seleccionados. Regresa al setup para configurar el quiz.');
      setGamePhase('setup');
      return;
    }

    playClickSound();

    const question = getRandomQuestion(regions, askedQuestions, difficulties, types);

    if (!question) {
      setAskedQuestions([]);
      const newQuestion = getRandomQuestion(regions, [], difficulties, types);
      setCurrentQuestion(newQuestion);
      if (newQuestion) setAskedQuestions([newQuestion.id]);
    } else {
      setCurrentQuestion(question);
      setAskedQuestions([...askedQuestions, question.id]);
    }

    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
    if (!gameStarted) setGameStarted(true);
  };

  const handleBackToSetup = () => {
    setGameStarted(false);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setGamePhase('setup');
  };

  const exitToLobby = useCallback(async () => {
    try {
      // Save progress before exiting if there's any
      await saveQuizProgress('exit');
    } catch (error) {
      console.error('Error guardando historial al salir:', error);
      setSaveStatus('error');
      setSaveMessage('Error de red');
      alert('Error al guardar tu progreso');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      // Always navigate to lobby and refresh it
      setLobbyKey(prev => prev + 1);
      setGamePhase('lobby');
      setShowExitConfirm(false);
    }
  }, [saveQuizProgress]);

  const handleExitConfirm = () => {
    setShowExitConfirm(true);
  };

  // Persist newly unlocked achievements for authenticated users
  useEffect(() => {
    const persistNew = async () => {
      if (!newAchievement || !isAuthenticated || !user) return;
      try {
        const { error } = await saveAchievement(user.id, newAchievement.id);
        if (error) {
          console.error('Error guardando logro:', error);
        } else {
          console.log('Logro guardado:', newAchievement.id);
        }
      } catch (e) {
        console.error('Error guardando logro:', newAchievement.id, e);
      }
    };
    persistNew();
  }, [newAchievement, isAuthenticated, user]);

  const toggleRegion = (regionId) => {
    setSelectedRegions(prev => {
      if (prev.includes(regionId)) {
        return prev.filter(r => r !== regionId);
      } else {
        return [...prev, regionId];
      }
    });
    setGameStarted(false);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setAskedQuestions([]);
  };

  const selectAllRegions = () => {
    setSelectedRegions(regions.map(r => r.id));
    setGameStarted(false);
  };

  const deselectAllRegions = () => {
    setSelectedRegions([]);
    setGameStarted(false);
  };

  // Memoized calculations for performance
  const accuracy = useMemo(
    () => (attempts > 0 ? Math.round((score / (attempts * 10)) * 100) : 0),
    [attempts, score]
  );

  const totalQuestions = useMemo(
    () => getQuestionsByRegions(selectedRegions, selectedDifficulties, selectedTypes).length,
    [selectedRegions, selectedDifficulties, selectedTypes]
  );

  // Helper para calcular logros a partir de un objeto de stats genérico
  const buildAchievementsFromStats = ({ attempts: baseAttempts, bestStreak: baseBestStreak, score: baseScore, accuracy: baseAccuracy }) => [
    {
      id: 'primer_paso',
      title: 'Primer Paso',
      description: 'Responde tu primera pregunta',
      unlocked: baseAttempts > 0
    },
    {
      id: 'racha_5',
      title: 'En Racha',
      description: 'Consigue una racha de 5',
      unlocked: baseBestStreak >= 5
    },
    {
      id: 'experto',
      title: 'Experto',
      description: 'Consigue una racha de 10',
      unlocked: baseBestStreak >= 10
    },
    {
      id: 'maestro',
      title: 'Maestro',
      description: 'Alcanza 100 puntos',
      unlocked: baseScore >= 100
    },
    {
      id: 'profesor',
      title: 'Profesor',
      description: 'Alcanza 500 puntos',
      unlocked: baseScore >= 500
    },
    {
      id: 'precision',
      title: 'Precisión',
      description: 'Mantén 80% de precisión con 10+ respuestas',
      unlocked: baseAttempts >= 10 && baseAccuracy >= 80
    },
    {
      id: 'dedicacion',
      title: 'Dedicación',
      description: 'Responde 50 preguntas',
      unlocked: baseAttempts >= 50
    },
    {
      id: 'anatomista',
      title: 'Anatomista',
      description: 'Responde 100 preguntas',
      unlocked: baseAttempts >= 100
    },
    {
      id: 'perfeccionista',
      title: 'Perfeccionista',
      description: '10 respuestas correctas seguidas',
      unlocked: baseBestStreak >= 10
    }
  ];

  // Logros de la sesión actual
  const achievements = useMemo(
    () =>
      buildAchievementsFromStats({
        attempts,
        bestStreak,
        score,
        accuracy
      }),
    [attempts, bestStreak, score, accuracy]
  );

  // Check for newly unlocked achievements
  useEffect(() => {
    achievements.forEach(achievement => {
      if (achievement.unlocked && !unlockedAchievements.has(achievement.id)) {
        setNewAchievement(achievement);
        setUnlockedAchievements(prev => new Set(prev).add(achievement.id));
      }
    });
  }, [achievements, unlockedAchievements]);

  const getButtonStyle = (index) => {
    if (!showFeedback) {
      return "bg-white hover:bg-purple-50 border-2 border-purple-200 hover:border-purple-400 text-gray-700 shadow-sm hover:shadow-md";
    }
    
    if (index === currentQuestion.correctAnswer) {
      return "bg-gradient-to-r from-green-200 to-emerald-200 border-2 border-green-400 text-green-800 shadow-md";
    }
    
    if (index === selectedAnswer && !isCorrect) {
      return "bg-gradient-to-r from-red-200 to-rose-200 border-2 border-red-400 text-red-800 shadow-md";
    }
    
    return "bg-gray-50 border-2 border-gray-200 text-gray-400 opacity-60";
  };

  const getRegionDisplayName = (regionId) => {
    const region = regions.find(r => r.id === regionId);
    return region ? region.name : regionId;
  };

  const getQuestionTypeLabel = (question) => {
    if (!question) return 'Selección simple';
    if (question.type === QUESTION_TYPES.VERDADERO_FALSO || question.options?.length === 2) {
      return 'Verdadero o Falso';
    }
    return 'Selección simple';
  };

  const getExamTimerClasses = (timeLeft, totalTime) => {
    if (!totalTime || timeLeft == null) {
      return {
        container: "flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 shadow-sm",
        spinner: "w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin",
        text: "text-xs sm:text-sm font-semibold text-emerald-700"
      };
    }

    const ratio = timeLeft / totalTime;

    if (ratio <= 0.2) {
      return {
        container: "flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-rose-50 to-red-50 border border-red-300 shadow-md animate-pulse",
        spinner: "w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-red-500 border-t-transparent animate-spin",
        text: "text-xs sm:text-sm font-semibold text-red-700"
      };
    }

    if (ratio <= 0.5) {
      return {
        container: "flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-300 shadow-sm animate-pulse",
        spinner: "w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-amber-400 border-t-transparent animate-spin",
        text: "text-xs sm:text-sm font-semibold text-amber-800"
      };
    }

    return {
      container: "flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 shadow-sm",
      spinner: "w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin",
      text: "text-xs sm:text-sm font-semibold text-emerald-700"
    };
  };

  const getExamGradeMessage = (grade) => {
    if (grade < 2) {
      return {
        label: 'Raspado y con vergüenza...',
        tone: 'text-red-700',
        bg: 'bg-red-50',
        badge: 'REPROBADO'
      };
    }
    if (grade >= 2 && grade < 5) {
      return {
        label: 'Raspado... pero estuviste cerca.',
        tone: 'text-amber-700',
        bg: 'bg-amber-50',
        badge: 'REPROBADO'
      };
    }
    if (grade >= 5 && grade < 7) {
      return {
        label: 'Aprobado... pero parece que te falta confianza.',
        tone: 'text-emerald-700',
        bg: 'bg-emerald-50',
        badge: 'APROBADO'
      };
    }
    if (grade >= 7 && grade < 8) {
      return {
        label: 'Aprobado... pero estás en una línea muy fina, probablemente aprendiste al caletre o qué sé yo...',
        tone: 'text-emerald-700',
        bg: 'bg-emerald-50',
        badge: 'APROBADO'
      };
    }
    if (grade >= 8 && grade < 10) {
      return {
        label: 'Aprobado... y honestamente, con muy excelente nota. No es perfecto, pero se nota que te has esforzado. ¡Felicidades!',
        tone: 'text-emerald-800',
        bg: 'bg-emerald-50',
        badge: 'APROBADO'
      };
    }
    return {
      label: '¡Wow! Estoy sin palabras. Eres digno de ser llamado estudiante de medicina, ¡tienes un gran futuro!',
      tone: 'text-purple-800',
      bg: 'bg-purple-50',
      badge: 'APROBADO CON HONORES'
    };
  };

  const getExamRegionFeedback = (regionId, stats) => {
    if (!stats || !stats.total) {
      return 'No hubo suficientes preguntas para evaluar esta región.';
    }
    const accuracy = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;

    if (accuracy < 40) {
      return 'Debes esforzarte mucho más en esta región. Es una zona débil que requiere estudio urgente.';
    }
    if (accuracy >= 40 && accuracy < 70) {
      return 'Tienes una base aceptable, pero aún cometes errores. Refuerza esta región para subir tu seguridad.';
    }
    if (accuracy >= 70 && accuracy < 90) {
      return 'Buen desempeño en esta región. Sigue repasando, pero puedes priorizar otras zonas más débiles.';
    }
    return 'Dominio excelente de esta región. No es necesario que te enfoques demasiado aquí; concentra tu energía en las partes más débiles.';
  };

  // Load accumulated stats from Supabase when modal opens
  const loadAccumulatedStats = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setAccumulatedStats(null);
      return;
    }

    setIsLoadingStats(true);
    try {
      const [historyResult, regionHistoryResult, userAchievementsResult] = await Promise.all([
        getQuizHistory(user.id, 100),
        getRegionHistory(user.id),
        getUserAchievements(user.id)
      ]);

      const history = historyResult.data;
      const error = historyResult.error;
      
      if (error) {
        console.error('Error cargando historial:', error);
        setAccumulatedStats(null);
        return;
      }

      if (!history || history.length === 0) {
        // No hay historial, usar stats de sesión actual
        setAccumulatedStats(null);
        return;
      }

      // Calcular stats acumulados de todo el historial
      const totalScore = history.reduce((sum, h) => sum + (h.score || 0), 0);
      const totalAttempts = history.reduce((sum, h) => sum + (h.attempts || 0), 0);
      const avgAccuracy = history.length > 0
        ? Math.round(history.reduce((sum, h) => sum + (Number(h.accuracy) || 0), 0) / history.length)
        : 0;

      // Calcular correctas e incorrectas estimadas
      const totalCorrect = Math.round(totalScore / 10); // Cada respuesta correcta = 10 puntos
      const totalIncorrect = totalAttempts - totalCorrect;

      // Agregar stats de región acumulados
      const accumulatedRegionStats = (regionHistoryResult.data || []).reduce((acc, row) => {
        const key = row.region;
        if (!acc[key]) {
          acc[key] = { correct: 0, incorrect: 0, total: 0 };
        }
        acc[key].correct += row.correct || 0;
        acc[key].incorrect += row.incorrect || 0;
        acc[key].total += row.total || 0;
        return acc;
      }, {});

      const accumulated = {
        score: totalScore,
        attempts: totalAttempts,
        accuracy: avgAccuracy,
        correctAnswers: totalCorrect,
        incorrectAnswers: totalIncorrect,
        gamesPlayed: history.length,
        // Usar best streak de la sesión actual como referencia
        bestStreak: bestStreak,
        streak: streak,
        regionStats: accumulatedRegionStats
      };

      setAccumulatedStats(accumulated);

      // Construir logros acumulados usando stats históricos
      const accumulatedAch = buildAchievementsFromStats({
        attempts: accumulated.attempts,
        bestStreak: accumulated.bestStreak,
        score: accumulated.score,
        accuracy: accumulated.accuracy
      }).map((ach) => ({
        ...ach,
        // Si existe en user_achievements lo consideramos desbloqueado aunque los números cambiaran
        unlocked: ach.unlocked || (userAchievementsResult.data || []).includes(ach.id)
      }));

      setAccumulatedAchievements(accumulatedAch);
    } catch (err) {
      console.error('Error cargando stats acumulados:', err);
      setAccumulatedStats(null);
    } finally {
      setIsLoadingStats(false);
    }
  }, [isAuthenticated, user, bestStreak, streak]);

  // Load stats when progress modal opens
  useEffect(() => {
    if (showProgressModal) {
      loadAccumulatedStats();
    }
  }, [showProgressModal, loadAccumulatedStats]);

  // Load app version from public/.version and decide whether to show version panel
  useEffect(() => {
    const loadVersionInfo = async () => {
      try {
        const response = await fetch('/.version', { cache: 'no-cache' });
        if (!response.ok) return;

        const text = await response.text();
        const lines = text.split('\n').map(line => line.trim()).filter(Boolean);

        let version = null;
        const changes = [];
        let hasSocialTutorialFlag = false;

        lines.forEach(line => {
          if (line.startsWith('version=')) {
            version = line.replace('version=', '').trim();
          } else if (line === 'tutorial_social=true') {
            hasSocialTutorialFlag = true;
          } else if (line.startsWith('-')) {
            changes.push(line.replace(/^-/, '').trim());
          }
        });

        if (!version) return;

        setAppVersion(version);
        setAppChanges(changes);

        const storageKey = 'anatomy_app_last_seen_version';
        const lastSeen = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;

        if (typeof window !== 'undefined' && hasSocialTutorialFlag) {
          const tutorialKey = `anatomy_social_tutorial_seen_${version}`;
          const tutorialSeen = window.localStorage.getItem(tutorialKey);
          setHasPendingSocialTutorial(!tutorialSeen);
        }

        if (lastSeen !== version) {
          setShowVersionPanel(true);
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(storageKey, version);
          }
        }
      } catch (e) {
        console.error('Error loading .version file', e);
      }
    };

    if (typeof window !== 'undefined') {
      loadVersionInfo();
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  useEffect(() => {
    if (gamePhase === 'lobby' && hasPendingSocialTutorial && !showSocialTutorial && isMobile) {
      setShowSocialTutorial(true);
      setSocialTutorialStep(0);
    }
  }, [gamePhase, hasPendingSocialTutorial, showSocialTutorial, isMobile]);

  useEffect(() => {
    if (!showSocialTutorial || !isMobile) return;
    if (typeof window === 'undefined') return;

    let target = null;
    if (socialTutorialStep === 0 || socialTutorialStep === 1) {
      target = bottomNavbarRef.current;
    } else if (socialTutorialStep === 2) {
      target = socialContainerRef.current;
    } else if (socialTutorialStep === 3) {
      target = socialFormRef.current;
    } else if (socialTutorialStep === 4) {
      target = socialActivityRef.current;
    }

    if (!target) return;

    const rect = target.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    if (rect.top < viewportHeight / 2) {
      setTutorialTooltipPlacement('bottom');
    } else {
      setTutorialTooltipPlacement('top');
    }
  }, [showSocialTutorial, isMobile, socialTutorialStep, gamePhase]);

  const handleAdvanceSocialTutorial = () => {
    setSocialTutorialStep(prevStep => {
      const nextStep = prevStep + 1;
      const totalSteps = 5; // 0..4

      if (nextStep === 2) {
        setGamePhase('social');
      }

      if (nextStep >= totalSteps) {
        if (typeof window !== 'undefined' && appVersion) {
          const tutorialKey = `anatomy_social_tutorial_seen_${appVersion}`;
          window.localStorage.setItem(tutorialKey, 'true');
        }
        setHasPendingSocialTutorial(false);
        setShowSocialTutorial(false);
        return prevStep;
      }

      return nextStep;
    });
  };

  return (
    <div className="min-h-screen">
      {/* Start Screen - Phase 1 */}
      {gamePhase === 'start' && (
        <StartScreen 
          onStart={handleStartClick}
          isAuthenticated={isAuthenticated}
        />
      )}

      {gamePhase === 'exam-playing' && (
        <div className="min-h-screen py-3 sm:py-4 px-2 sm:px-3 md:px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-4 sm:mb-6">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 bg-white/90 rounded-2xl shadow-lg">
                  <Brain className="w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 text-purple-400" />
                </div>
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-700 drop-shadow-sm">
                  Examen de Anatomía
                </h1>
              </div>

              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-4">
                <button
                  onClick={() => setShowProgressModal(true)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-br from-purple-100 to-pink-100 hover:from-purple-200 hover:to-pink-200 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 border-2 border-purple-300"
                  title="Ver mi progreso"
                >
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  <span className="font-semibold text-purple-700 text-xs sm:text-sm">Perfil</span>
                </button>

                <button
                  onClick={() => setGamePhase('exam-setup')}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-br from-blue-100 to-cyan-100 hover:from-blue-200 hover:to-cyan-200 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 border-2 border-blue-300"
                  title="Cambiar configuración de examen"
                >
                  <Focus className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  <span className="font-semibold text-blue-700 text-xs sm:text-sm">Config. Examen</span>
                </button>

                <button
                  onClick={() => setGamePhase('lobby')}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 border-2 border-white/30"
                  title="Salir al lobby"
                >
                  <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-semibold text-xs sm:text-sm">Salir</span>
                </button>
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl p-3 sm:p-4 md:p-6 lg:p-9 border border-purple-100">
              {examFinished ? (
                examResultPhase === 'evaluating' ? (
                  <div className="flex flex-col items-center justify-center py-10 sm:py-16 gap-6 animate-fade-in">
                    <div className="relative w-32 h-32 sm:w-40 sm:h-40">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-purple-200 via-pink-200 to-blue-200 opacity-60 blur-xl animate-pulse" />
                      <div className="relative w-full h-full rounded-full border-4 border-dashed border-purple-300 flex items-center justify-center bg-white/80 shadow-xl animate-spin-slow">
                        <Brain className="w-10 h-10 sm:w-12 sm:h-12 text-purple-500" />
                      </div>
                    </div>

                    <div className="space-y-3 sm:space-y-4 text-center">
                      <p className="text-lg sm:text-2xl font-extrabold text-gray-900 tracking-tight">
                        {examEvaluationStep === 0 && 'El profesor está evaluando tus respuestas...'}
                        {examEvaluationStep === 1 && 'Comparando tus resultados con el estándar clínico...'}
                        {examEvaluationStep === 2 && 'Realizando análisis final de tu desempeño...'}
                      </p>
                      <p className="text-sm sm:text-base text-gray-600 max-w-xl mx-auto">
                        Mantente atento. En unos segundos verás un certificado detallado con tu nota final y un análisis por regiones del cuerpo.
                      </p>
                    </div>

                    <div className="flex gap-2 sm:gap-3 mt-2">
                      <span className="w-3 h-3 rounded-full bg-purple-400 animate-bounce" />
                      <span className="w-3 h-3 rounded-full bg-pink-400 animate-bounce delay-150" />
                      <span className="w-3 h-3 rounded-full bg-blue-400 animate-bounce delay-300" />
                    </div>
                  </div>
                ) : (
                  <div className="relative overflow-hidden rounded-3xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 via-white to-indigo-50 shadow-2xl">
                    <div className="pointer-events-none absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,_#e9d5ff_0,_transparent_55%),_radial-gradient(circle_at_bottom,_#f9a8d4_0,_transparent_55%)]" />

                    <div className="relative p-5 sm:p-7 md:p-10 space-y-5 sm:space-y-7">
                      <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4 sm:gap-6">
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className="p-2.5 sm:p-3.5 bg-white/80 rounded-2xl shadow-lg border border-purple-100">
                            <Award className="w-7 h-7 sm:w-9 sm:h-9 text-purple-500" />
                          </div>
                          <div className="text-left">
                            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-purple-400 font-semibold">Certificado de evaluación</p>
                            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 leading-tight">
                              Examen de Anatomía Médica
                            </h2>
                          </div>
                        </div>

                        {(() => {
                          const totalQuestions = examConfig?.total || examQuestions.length || 1;
                          const rawGrade = (examCorrectCount / totalQuestions) * 10;
                          const grade = Number(rawGrade.toFixed(2));
                          const gradeInfo = getExamGradeMessage(grade);

                          return (
                            <div className={`px-4 sm:px-5 py-3 sm:py-3.5 rounded-2xl border shadow-inner flex flex-col items-center min-w-[140px] ${gradeInfo.bg}`}>
                              <span className="text-[0.65rem] sm:text-xs font-semibold tracking-[0.3em] text-gray-500 uppercase">Estado</span>
                              <span className="mt-1 text-xs sm:text-sm font-bold text-gray-800 tracking-wide">
                                {gradeInfo.badge}
                              </span>
                              <span className="mt-2 text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 leading-none">
                                {grade.toFixed(2)}
                              </span>
                              <span className="text-[0.65rem] sm:text-xs text-gray-500 mt-1">sobre 10</span>
                            </div>
                          );
                        })()}
                      </div>

                      {(() => {
                        const totalQuestions = examConfig?.total || examQuestions.length || 1;
                        const rawGrade = (examCorrectCount / totalQuestions) * 10;
                        const grade = Number(rawGrade.toFixed(2));
                        const gradeInfo = getExamGradeMessage(grade);

                        return (
                          <div className={`rounded-2xl px-4 sm:px-5 py-3 sm:py-4 border ${gradeInfo.bg} ${gradeInfo.tone} shadow-sm`}
                          >
                            <p className="text-sm sm:text-base md:text-lg font-semibold">
                              {gradeInfo.label}
                            </p>
                          </div>
                        );
                      })()}

                      <div className="grid grid-cols-1 md:grid-cols-5 gap-5 sm:gap-6 items-start">
                        <div className="md:col-span-3 space-y-4 sm:space-y-5">
                          <div>
                            <h3 className="text-sm sm:text-base font-bold text-gray-800 tracking-wide flex items-center gap-2">
                              <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                              Resumen del desempeño
                            </h3>
                            <p className="mt-1 text-xs sm:text-sm text-gray-600">
                              Respondiste correctamente <span className="font-semibold text-gray-900">{examCorrectCount}</span> de{' '}
                              <span className="font-semibold text-gray-900">{examConfig?.total || examQuestions.length}</span> preguntas en el tiempo asignado.
                            </p>
                          </div>

                          <div className="mt-3 sm:mt-4 space-y-3">
                            <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
                              <span>Progreso general</span>
                              <span className="font-semibold text-gray-800">
                                {examCorrectCount} / {examConfig?.total || examQuestions.length} correctas
                              </span>
                            </div>
                            {(() => {
                              const totalQuestions = examConfig?.total || examQuestions.length || 1;
                              const accuracy = Math.round((examCorrectCount / totalQuestions) * 100);
                              return (
                                <div className="w-full h-3 sm:h-3.5 rounded-full bg-gray-100 overflow-hidden shadow-inner">
                                  <div
                                    className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-amber-300 to-rose-400 transition-all duration-700"
                                    style={{ width: `${accuracy}%` }}
                                  />
                                </div>
                              );
                            })()}
                          </div>

                          <p className="text-xs sm:text-sm text-gray-500 italic">
                            Este certificado resume tu rendimiento en este examen específico. Úsalo como guía para enfocar mejor tu estudio.
                          </p>

                          <div className="mt-4 sm:mt-5 rounded-2xl bg-white/80 border border-purple-100 shadow-md p-3 sm:p-4 space-y-3 max-h-64 sm:max-h-72 overflow-y-auto">
                            <h3 className="text-sm sm:text-base font-bold text-gray-800 flex items-center gap-2">
                              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                              Detalle de preguntas del examen
                            </h3>

                            {examAnswers.length === 0 && (
                              <p className="text-xs sm:text-sm text-gray-500">
                                No se registraron detalles de las respuestas en este examen.
                              </p>
                            )}

                            {examAnswers.length > 0 && (
                              <div className="space-y-2">
                                {examAnswers.map((answer, index) => {
                                  const explanation = answer.explanation || questionExplanations[answer.questionId];
                                  const selectedOption = answer.options[answer.selectedAnswerIndex];
                                  const correctOption = answer.options[answer.correctAnswerIndex];

                                  return (
                                    <div
                                      key={answer.questionId || index}
                                      className="rounded-xl border px-3 py-2.5 sm:px-3.5 sm:py-3 bg-gradient-to-br from-gray-50 to-purple-50/60"
                                    >
                                      <div className="flex items-center justify-between gap-2 mb-1">
                                        <span className="text-[0.7rem] sm:text-xs font-semibold text-gray-500 uppercase">
                                          Pregunta {index + 1}
                                        </span>
                                        <span
                                          className={`text-[0.7rem] sm:text-xs font-bold px-2 py-0.5 rounded-full ${
                                            answer.isCorrect
                                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                              : 'bg-rose-100 text-rose-700 border border-rose-200'
                                          }`}
                                        >
                                          {answer.isCorrect ? 'Correcta' : 'Incorrecta'}
                                        </span>
                                      </div>

                                      <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-1.5">
                                        {answer.question}
                                      </p>

                                      <div className="space-y-0.5 mb-1.5">
                                        <p className="text-[0.7rem] sm:text-xs text-gray-600">
                                          <span className="font-semibold">Tu respuesta:</span>{' '}
                                          <span className={answer.isCorrect ? 'text-emerald-700' : 'text-rose-700'}>
                                            {selectedOption}
                                          </span>
                                        </p>
                                        {!answer.isCorrect && (
                                          <p className="text-[0.7rem] sm:text-xs text-gray-600">
                                            <span className="font-semibold">Respuesta correcta:</span>{' '}
                                            <span className="text-emerald-700">{correctOption}</span>
                                          </p>
                                        )}
                                      </div>

                                      <p className="text-[0.7rem] sm:text-xs text-gray-600">
                                        <span className="font-semibold">Explicación:</span>{' '}
                                        {explanation || 'Explicación pendiente para esta pregunta.'}
                                      </p>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="md:col-span-2">
                          <div className="rounded-2xl bg-white/80 border border-purple-100 shadow-md p-4 sm:p-5 space-y-3">
                            <h3 className="text-sm sm:text-base font-bold text-gray-800 flex items-center gap-2">
                              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                              Análisis por regiones
                            </h3>

                            <div className="space-y-3 max-h-64 sm:max-h-72 overflow-y-auto pr-1 sm:pr-2">
                              {Object.keys(examRegionStats).length === 0 && (
                                <p className="text-xs sm:text-sm text-gray-500">
                                  No hubo suficientes preguntas para construir un análisis detallado por regiones en este examen.
                                </p>
                              )}

                              {Object.entries(examRegionStats).map(([regionId, stats]) => {
                                const typedStats = stats;
                                const accuracy = typedStats.total > 0 ? Math.round((typedStats.correct / typedStats.total) * 100) : 0;
                                const regionName = getRegionDisplayName(regionId);
                                const feedback = getExamRegionFeedback(regionId, typedStats);

                                return (
                                  <div key={regionId} className="rounded-xl bg-gradient-to-br from-purple-50/80 to-blue-50/80 border border-purple-100 px-3 py-2.5 sm:px-3.5 sm:py-3">
                                    <div className="flex items-center justify-between gap-2 mb-1.5">
                                      <span className="text-xs sm:text-sm font-semibold text-gray-800">
                                        {regionName}
                                      </span>
                                      <span className="text-xs sm:text-sm font-semibold text-purple-700">
                                        {accuracy}%
                                      </span>
                                    </div>
                                    <div className="w-full h-2.5 rounded-full bg-white/80 overflow-hidden mb-1">
                                      <div
                                        className="h-full rounded-full bg-gradient-to-r from-red-400 via-amber-400 to-emerald-400 transition-all duration-700"
                                        style={{ width: `${accuracy}%` }}
                                      />
                                    </div>
                                    <p className="text-[0.7rem] sm:text-xs text-gray-600 leading-snug">
                                      {feedback}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="pt-2 sm:pt-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-purple-100 mt-2 sm:mt-3">
                        <p className="text-[0.7rem] sm:text-xs text-gray-500 text-center sm:text-left">
                          Este documento es una representación visual de tu desempeño. No sustituye la evaluación formal de un profesor, pero te orienta sobre dónde reforzar.
                        </p>
                        <div className="flex gap-2 sm:gap-3">
                          <button
                            onClick={() => setGamePhase('exam-setup')}
                            className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-xs sm:text-sm font-semibold shadow-lg hover:shadow-xl transition-all"
                          >
                            Repetir examen
                          </button>
                          <button
                            onClick={() => setGamePhase('lobby')}
                            className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs sm:text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                          >
                            Volver al lobby
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : examCountdown > 0 ? (
                <div className="flex flex-col items-center justify-center py-10 sm:py-16 gap-6">
                  <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-purple-300 flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50 shadow-inner animate-pulse">
                    <span className="text-4xl sm:text-5xl font-bold text-purple-600">
                      {examCountdown}
                    </span>
                  </div>
                  <p className="text-lg sm:text-2xl font-bold text-gray-800">El examen comenzará en...</p>
                  <p className="text-sm sm:text-base text-gray-600 max-w-md text-center">
                    Prepárate: se activará el temporizador total del examen y deberás responder todas las preguntas dentro del tiempo.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-4 sm:mb-5 flex items-center justify-center">
                    {(() => {
                      const timerClasses = getExamTimerClasses(examTimeLeft, examTotalTime);
                      return (
                        <div className={timerClasses.container}>
                          <div className={timerClasses.spinner} />
                          <span className={timerClasses.text}>
                            Tiempo restante: {Math.floor(examTimeLeft / 60).toString().padStart(2, '0')}
                            :{(examTimeLeft % 60).toString().padStart(2, '0')}
                          </span>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="mb-4 sm:mb-5 flex justify-between items-center text-xs sm:text-sm bg-gradient-to-r from-purple-50 to-pink-50 p-2 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl border border-purple-200">
                    <span className="font-bold text-gray-600 flex items-center gap-1 sm:gap-1.5">
                      <Target className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400" />
                      Pregunta {examCurrentIndex + 1} de {examConfig?.total || examQuestions.length}
                    </span>
                    <span className="font-bold text-gray-600 flex items-center gap-1 sm:gap-1.5">
                      {getRegionDisplayName(currentQuestion?.region)}
                    </span>
                  </div>

                  {currentQuestion && (
                    <div className="mb-4 sm:mb-6">
                      <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-7 lg:p-9 mb-4 sm:mb-5 md:mb-6 shadow-lg border-2 border-purple-200">
                        <p className="text-[0.65rem] sm:text-xs font-semibold tracking-wide uppercase text-purple-500 mb-1">
                          {getQuestionTypeLabel(currentQuestion)}
                        </p>
                        <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-700 leading-tight">
                          {currentQuestion.question}
                        </h2>
                      </div>

                      <div className="grid grid-cols-1 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-5 md:mb-6">
                        {currentQuestion.options.map((option, index) => (
                          <button
                            key={index}
                            onClick={() => handleExamAnswerClick(index)}
                            className="p-3 sm:p-4 md:p-5 lg:p-6 rounded-lg sm:rounded-xl md:rounded-2xl font-semibold text-sm sm:text-base md:text-lg text-left transition-all transform hover:scale-[1.02] active:scale-[0.98] bg-white hover:bg-purple-50 border-2 border-purple-100 hover:border-purple-300 shadow-sm hover:shadow-md"
                          >
                            <span className="flex-1">{option}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showSocialTutorial && isMobile && (
        <div
          className={`fixed inset-0 z-[60] bg-black/70 flex justify-center px-3 ${tutorialTooltipPlacement === 'top' ? 'items-start pt-6' : 'items-end pb-6 sm:pb-10'}`}
        >
          <div className="max-w-md w-full bg-white/95 rounded-2xl shadow-2xl p-4 sm:p-5 border border-purple-200 relative z-[80]">
            <p className="text-xs sm:text-sm text-gray-700 mb-3">
              {socialTutorialStep === 0 && 'Este es el navbar principal. Desde aquí podrás moverte entre el juego, tu perfil y la sección Social.'}
              {socialTutorialStep === 1 && 'En el navbar y en el lobby encontrarás las opciones para entrar al juego: modo Quiz de práctica o Examen de evaluación.'}
              {socialTutorialStep === 2 && 'Al seleccionar Social, llegas a este espacio donde verás lo que ocurre en Quiz-Anatomia y podrás compartir tu propio progreso.'}
              {socialTutorialStep === 3 && 'En esta zona puedes crear un post: escribe un mensaje, añade imágenes o audio y publica tus avances o dudas para otros estudiantes.'}
              {socialTutorialStep === 4 && 'Aquí se muestra la Actividad reciente: publicaciones de otros estudiantes y posts del sistema con certificados de evaluación para que todos puedan aprender.'}
            </p>
            <button
              type="button"
              onClick={handleAdvanceSocialTutorial}
              className="mt-1 inline-flex items-center justify-center w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs sm:text-sm font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              {socialTutorialStep === 4 ? 'Finalizar tutorial' : 'Siguiente'}
            </button>
          </div>
        </div>
      )}

      {/* Profile Setup - Phase 2 (for new users) */}
      {gamePhase === 'profile-setup' && (
        <ProfileSetup
          onComplete={handleProfileComplete}
          userEmail={user?.email}
          initialData={profile}
        />
      )}

      {/* Quiz Setup - Phase 3 */}
      {gamePhase === 'setup' && (
        <QuizSetup
          onStartQuiz={handleQuizSetup}
          totalQuestions={totalQuestions}
        />
      )}

      {gamePhase === 'exam-setup' && (
        <ExamSetup
          onStartExam={handleExamSetup}
        />
      )}

      {/* Lobby - Phase 3 (nuevo) */}
      {gamePhase === 'lobby' && (
        <Lobby 
          key={lobbyKey}
          onGoToSetup={() => setGamePhase('setup')}
          onGoToExam={() => setGamePhase('exam-setup')}
          onShowProgress={() => setShowProgressModal(true)}
        />
      )}

      {/* Social - Phase 4 */}
      {gamePhase === 'social' && (
        <div className="min-h-screen py-3 sm:py-4 px-2 sm:px-3 md:px-4 relative">
          <div
            ref={socialContainerRef}
            className={`max-w-6xl mx-auto ${showSocialTutorial && socialTutorialStep === 2 ? 'relative z-[70]' : ''}`}
          >
            <div className="text-center mb-4 sm:mb-6">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="p-2.5 sm:p-3.5 bg-gradient-to-br from-rose-100 via-pink-50 to-purple-100 rounded-3xl shadow-xl border border-rose-200/60">
                  <Heart className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-rose-500" />
                </div>
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-800 tracking-tight drop-shadow-sm">
                  Social
                </h1>
              </div>

              <p className="text-xs sm:text-sm text-gray-600 max-w-xl mx-auto">
                Comparte tus avances, pensamientos o preguntas con otros estudiantes. Puedes publicar texto, una imagen y/o un audio.
              </p>
            </div>

            {otherSocialUsers.length > 0 && (
              <div className="mb-3 sm:mb-4 flex items-center gap-2 overflow-x-auto py-1">
                {otherSocialUsers.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      alert('Próximamente podrás etiquetar a este usuario y ver su progreso y ranking.');
                    }}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-slate-200 via-slate-100 to-white border border-slate-200 text-[0.7rem] font-semibold text-slate-700 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                    title={id}
                  >
                    {(id || '?').slice(0, 2).toUpperCase()}
                  </button>
                ))}
              </div>
            )}

            <div className="mb-4 sm:mb-6">
              <form
                onSubmit={handleCreatePost}
                className="relative overflow-hidden bg-white/95 backdrop-blur-sm rounded-3xl shadow-[0_18px_45px_rgba(148,27,128,0.18)] p-3 sm:p-4 md:p-5 border border-rose-100"
              >
                <div className="pointer-events-none absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,_#fce7f3_0,_transparent_55%),_radial-gradient(circle_at_bottom,_#e0e7ff_0,_transparent_55%)]" />

                <div className="relative space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.65rem] font-medium bg-rose-50 text-rose-600 border border-rose-100">
                        <Heart className="w-3.5 h-3.5" />
                        Nuevo post
                      </span>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-[0.7rem] font-bold text-white overflow-hidden border border-white/70 shadow-sm">
                          {profile?.avatar_url ? (
                            <img
                              src={profile.avatar_url}
                              alt="Avatar"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            (profile?.initials || profile?.username?.[0] || user?.email?.[0] || 'U').toUpperCase()
                          )}
                        </div>
                        <div className="flex flex-col items-start">
                          <span className="text-[0.7rem] sm:text-xs font-semibold text-gray-800 leading-tight">
                            {profile?.username || profile?.display_name || user?.email || 'Usuario'}
                          </span>
                          <span className="text-[0.6rem] text-gray-400 leading-tight flex items-center gap-1">
                            {userRankLabel === 'Top 1' ? (
                              <>
                                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-yellow-400 text-yellow-900">
                                  <Crown className="w-3 h-3" />
                                </span>
                                <span className="font-semibold text-yellow-700">Top 1</span>
                              </>
                            ) : userRankLabel ? (
                              <span className="font-semibold text-purple-600">{userRankLabel}</span>
                            ) : (
                              <span>Sin rango asignado</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span className="text-[0.65rem] sm:text-[0.7rem] text-gray-400">
                      Comparte algo breve y útil
                    </span>
                  </div>

                  <input
                    type="text"
                    value={newPostTitle}
                    onChange={(e) => setNewPostTitle(e.target.value)}
                    className="w-full rounded-2xl border border-purple-100/80 bg-gradient-to-br from-slate-50 via-white to-purple-50/60 px-3.5 py-2 text-xs sm:text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400/70 focus:border-purple-300 shadow-inner mb-2"
                    placeholder="Título del post (puedes usar #tags aquí)"
                  />

                  <textarea
                    value={newPostBody}
                    onChange={(e) => setNewPostBody(e.target.value)}
                    rows={3}
                    className="w-full rounded-2xl border border-purple-100/80 bg-gradient-to-br from-slate-50 via-white to-purple-50/60 px-3.5 py-2.5 text-xs sm:text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400/70 focus:border-purple-300 shadow-inner resize-none max-h-24 overflow-y-auto"
                    placeholder="Escribe el contenido del mensaje (puedes usar #tags aquí también)"
                  />

                  <div className="flex items-center justify-between gap-2 pt-1">
                    <div className="flex items-center gap-2">
                      <label className="relative inline-flex items-center justify-center w-8 h-8 rounded-full border border-purple-200 bg-purple-50/70 text-purple-500 hover:border-purple-400 hover:bg-purple-100 cursor-pointer transition-colors">
                        <Image className="w-4 h-4" />
                        {newPostImageFile && (
                          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 border border-white" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setNewPostImageFile(e.target.files?.[0] || null)}
                          className="hidden"
                        />
                      </label>
                      <label className="relative inline-flex items-center justify-center w-8 h-8 rounded-full border border-purple-200 bg-purple-50/70 text-purple-500 hover:border-purple-400 hover:bg-purple-100 cursor-pointer transition-colors">
                        <Mic className="w-4 h-4" />
                        {newPostAudioFile && (
                          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 border border-white" />
                        )}
                        <input
                          type="file"
                          accept="audio/*"
                          onChange={(e) => setNewPostAudioFile(e.target.files?.[0] || null)}
                          className="hidden"
                        />
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-500 to-purple-500 hover:from-pink-600 hover:via-rose-600 hover:to-purple-600 text-white text-xs sm:text-sm font-semibold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 active:translate-y-0 border border-white/40"
                    >
                      <Heart className="w-4 h-4" />
                      Publicar
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div
              ref={socialActivityRef}
              className={`bg-white/95 backdrop-blur-sm rounded-3xl shadow-[0_16px_40px_rgba(15,23,42,0.08)] p-3 sm:p-4 md:p-5 border border-slate-100 ${showSocialTutorial && socialTutorialStep === 4 ? 'relative z-[70]' : ''}`}
            >
              <h2 className="text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                Actividad reciente
              </h2>

              {isLoadingSocial && (
                <p className="text-xs sm:text-sm text-gray-500">Cargando actividad social...</p>
              )}

              {!isLoadingSocial && socialError && (
                <p className="text-xs sm:text-sm text-rose-600">{socialError}</p>
              )}

              {!isLoadingSocial && !socialError && socialPosts.length === 0 && (
                <p className="text-xs sm:text-sm text-gray-500">
                  Aún no hay publicaciones. Sé el primero en compartir algo.
                </p>
              )}

              <div className="mt-2 sm:mt-3 space-y-3 max-h-[520px] overflow-y-auto pr-1 sm:pr-1.5">
                {socialPosts.map((post) => {
                  const media = post.social_post_media || [];
                  const replies = post.social_post_replies || [];
                  const reactions = post.social_post_reactions || [];
                  const heartsCount = reactions.length;
                  const userHasLiked = isAuthenticated && user && reactions.some((r) => r.user_id === user.id);
                  const isOwnPost = user && post.user_id === user.id;

                  const rawContent = post.content || '';
                  const isSystemExamPost = rawContent.startsWith('__SYSTEM_EXAM_CERT__');

                  const postDisplayName = isSystemExamPost
                    ? 'Sistema · Quiz-Anatomia'
                    : isOwnPost
                      ? (profile?.username || profile?.display_name || user.email || 'Tú')
                      : (post.user_id ? `@${(post.user_id.slice(0, 6) || 'usr')}` : '@usuario');

                  let title = '';
                  let message = '';
                  let tags = [];
                  let systemHeader = '';
                  let systemDetail = '';

                  if (isSystemExamPost) {
                    const withoutMarker = rawContent.replace(/^__SYSTEM_EXAM_CERT__\n?/, '');
                    const parts = withoutMarker.split('\n---DETALLE---\n');
                    systemHeader = (parts[0] || '').trim();
                    systemDetail = (parts[1] || '').trim();
                  } else {
                    const lines = rawContent.split('\n');
                    title = (lines[0] || '').trim();
                    message = lines.slice(1).join('\n').trim();
                    const tagMatches = rawContent.match(/#(\w+)/g) || [];
                    tags = tagMatches.map((t) => t.replace('#', ''));
                  }

                  return (
                    <div
                      key={post.id}
                      className="rounded-2xl border border-slate-100 bg-gradient-to-br from-slate-50 via-white to-rose-50/70 p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-slate-200 to-white flex items-center justify-center text-[0.7rem] font-semibold text-slate-700 overflow-hidden border border-white/70 shadow-sm">
                            {isOwnPost && profile?.avatar_url ? (
                              <img
                                src={profile.avatar_url}
                                alt="Avatar"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              (isOwnPost
                                ? (profile?.username?.[0] || user?.email?.[0] || 'U')
                                : (post.user_id?.[0] || 'U')
                              ).toUpperCase()
                            )}
                          </div>
                          <div className="flex flex-col items-start">
                            <span className="text-[0.7rem] sm:text-xs font-semibold text-gray-800 leading-tight">
                              {postDisplayName}
                            </span>
                            <span className="text-[0.6rem] text-gray-400 leading-tight flex items-center gap-1">
                              {!isSystemExamPost && isOwnPost && userRankLabel === 'Top 1' && (
                                <>
                                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-yellow-400 text-yellow-900">
                                    <Crown className="w-3 h-3" />
                                  </span>
                                  <span className="font-semibold text-yellow-700">Top 1</span>
                                </>
                              )}
                              {!isSystemExamPost && isOwnPost && userRankLabel && userRankLabel !== 'Top 1' && (
                                <span className="font-semibold text-purple-600">{userRankLabel}</span>
                              )}
                              {isOwnPost && profile?.is_admin && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900 text-[0.55rem] font-semibold tracking-wide text-white">
                                  ADMIN
                                </span>
                              )}
                              {isSystemExamPost && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-[0.55rem] font-semibold tracking-wide text-purple-700">
                                  Publicación del sistema
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                        <span className="text-[0.65rem] sm:text-[0.7rem] text-gray-400">
                          {new Date(post.created_at).toLocaleString()}
                        </span>
                      </div>

                      {isSystemExamPost ? (
                        <div className="mb-2 space-y-2">
                          {systemHeader && (
                            <div className="rounded-xl bg-gradient-to-br from-purple-50 via-white to-indigo-50 border border-purple-100 px-3 py-2">
                              <p className="text-[0.75rem] sm:text-sm text-gray-800 whitespace-pre-wrap">
                                {systemHeader}
                              </p>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => setExpandedSystemPostId(prev => prev === post.id ? null : post.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.7rem] sm:text-xs font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md hover:shadow-lg transition-all"
                          >
                            <span>{expandedSystemPostId === post.id ? 'Ocultar detalle' : 'Ver con más detalle'}</span>
                          </button>
                          {expandedSystemPostId === post.id && systemDetail && (
                            <div className="mt-2 rounded-xl bg-white/90 border border-purple-100 px-3 py-2 max-h-64 overflow-y-auto">
                              <p className="text-[0.7rem] sm:text-xs text-gray-700 whitespace-pre-wrap">
                                {systemDetail}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : ((title || message) && (
                        <div className="mb-2 space-y-1.5">
                          {title && (
                            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 leading-snug">
                              {title}
                            </h3>
                          )}
                          {tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {tags.map((tag) => (
                                <span
                                  key={tag}
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.6rem] sm:text-[0.65rem] font-semibold border ${getTagColorClass(tag)}`}
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {message && (
                            <p className="text-[0.75rem] sm:text-sm text-gray-800 whitespace-pre-wrap">
                              {message}
                            </p>
                          )}
                          {!message && !tags.length && !title && rawContent && (
                            <p className="text-[0.75rem] sm:text-sm text-gray-800 whitespace-pre-wrap">
                              {rawContent}
                            </p>
                          )}
                        </div>
                      ))}

                      {media.length > 0 && (
                        <div className="space-y-2 mb-2">
                          {media.map((m) => (
                            <div key={m.id} className="rounded-xl overflow-hidden border border-slate-100 bg-white/80 p-2">
                              {m.media_type === 'image' && (
                                <img
                                  src={m.url}
                                  alt="Imagen del post"
                                  className="w-full max-h-64 object-cover rounded-lg"
                                />
                              )}
                              {m.media_type === 'audio' && (
                                <audio
                                  controls
                                  src={m.url}
                                  className="w-full"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between gap-2 mt-1">
                        <button
                          type="button"
                          onClick={async () => {
                            if (!isAuthenticated || !user) {
                              alert('Debes iniciar sesión para reaccionar.');
                              return;
                            }
                            const { liked, error } = await togglePostHeart(user.id, post.id);
                            if (error) {
                              console.error('Error al reaccionar:', error);
                              return;
                            }
                            setSocialPosts((prev) =>
                              prev.map((p) => {
                                if (p.id !== post.id) return p;
                                const current = p.social_post_reactions || [];
                                if (liked) {
                                  return {
                                    ...p,
                                    social_post_reactions: [
                                      ...current,
                                      { id: Math.random().toString(36).slice(2), user_id: user.id, created_at: new Date().toISOString() },
                                    ],
                                  };
                                }
                                return {
                                  ...p,
                                  social_post_reactions: current.filter((r) => r.user_id !== user.id),
                                };
                              })
                            );
                          }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.7rem] sm:text-xs font-semibold transition-all border ${
                            userHasLiked
                              ? 'bg-rose-500 text-white border-rose-500 shadow-md'
                              : 'bg-white/80 text-rose-600 border-rose-200 hover:bg-rose-50'
                          }`}
                        >
                          <Heart className={`w-3.5 h-3.5 ${userHasLiked ? 'fill-current' : ''}`} />
                          <span>{heartsCount}</span>
                        </button>

                        <div className="flex items-center gap-2">
                          <span className="text-[0.7rem] sm:text-xs text-gray-500">
                          {replies.length === 0
                            ? 'Sin respuestas aún'
                            : replies.length === 1
                            ? '1 respuesta'
                            : `${replies.length} respuestas`}
                          </span>

                          {(isOwnPost || profile?.is_admin) && (
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleEditPost(post)}
                                className="text-[0.65rem] sm:text-[0.7rem] px-2 py-1 rounded-full border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeletePost(post)}
                                className="text-[0.65rem] sm:text-[0.7rem] px-2 py-1 rounded-full border border-rose-200 text-rose-600 bg-white hover:bg-rose-50 hover:border-rose-300 transition-colors"
                              >
                                Eliminar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Playing - Phase 4 */}
      {gamePhase === 'playing' && (
        <div className="min-h-screen py-3 sm:py-4 px-2 sm:px-3 md:px-4">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-4 sm:mb-6">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 bg-white/90 rounded-2xl shadow-lg">
                  <Brain className="w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 text-purple-400" />
                </div>
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-700 drop-shadow-sm">
                  Quiz de Anatomía Médica
                </h1>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-4">
                <button
                  onClick={() => setShowProgressModal(true)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-br from-purple-100 to-pink-100 hover:from-purple-200 hover:to-pink-200 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 border-2 border-purple-300"
                  title="Ver mi progreso"
                >
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  <span className="font-semibold text-purple-700 text-xs sm:text-sm">Perfil</span>
                </button>

                <button
                  onClick={handleBackToSetup}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-br from-blue-100 to-cyan-100 hover:from-blue-200 hover:to-cyan-200 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 border-2 border-blue-300"
                  title="Cambiar configuración"
                >
                  <Focus className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  <span className="font-semibold text-blue-700 text-xs sm:text-sm">Setup</span>
                </button>

                <button
                  onClick={handleExitConfirm}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 border-2 border-white/30"
                  title="Salir al ranking"
                >
                  <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-semibold text-xs sm:text-sm">Salir</span>
                </button>
              </div>
            </div>

            {/* Main Game Area */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl p-3 sm:p-4 md:p-6 lg:p-9 border border-purple-100">
              <div>
              {/* Question Counter */}
              <div className="mb-4 sm:mb-5 flex justify-between items-center text-xs sm:text-sm bg-gradient-to-r from-purple-50 to-pink-50 p-2 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl border border-purple-200">
                <span className="font-bold text-gray-600 flex items-center gap-1 sm:gap-1.5">
                  <Target className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400" />
                  Pregunta {attempts + 1}
                </span>
                <span className="font-bold text-gray-600 flex items-center gap-1 sm:gap-1.5">
                  {getRegionDisplayName(currentQuestion?.region)}
                </span>
              </div>

              {/* Question */}
              {currentQuestion && (
                <div className="mb-4 sm:mb-6">
                  <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-7 lg:p-9 mb-4 sm:mb-5 md:mb-6 shadow-lg border-2 border-purple-200">
                    <p className="text-[0.65rem] sm:text-xs font-semibold tracking-wide uppercase text-purple-500 mb-1">
                      {getQuestionTypeLabel(currentQuestion)}
                    </p>
                    <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-700 leading-tight">
                      {currentQuestion.question}
                    </h2>
                  </div>

                  {/* Answer Options */}
                  <div className="grid grid-cols-1 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-5 md:mb-6">
                    {currentQuestion.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswerClick(index)}
                        disabled={showFeedback}
                        className={`p-3 sm:p-4 md:p-5 lg:p-6 rounded-lg sm:rounded-xl md:rounded-2xl font-semibold text-sm sm:text-base md:text-lg text-left transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:hover:scale-100 disabled:cursor-not-allowed ${getButtonStyle(index)}`}
                      >
                        <div className="flex items-center justify-between gap-2 sm:gap-3">
                          <span className="flex-1">{option}</span>
                          {showFeedback && index === currentQuestion.correctAnswer && (
                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 flex-shrink-0 text-green-600" />
                          )}
                          {showFeedback && index === selectedAnswer && !isCorrect && (
                            <XCircle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 flex-shrink-0 text-red-600" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Explanation Card */}
                  {showFeedback && (
                    <ExplanationCard
                      isCorrect={isCorrect}
                      question={currentQuestion}
                      selectedAnswer={selectedAnswer}
                      onContinue={startNewRoundWithConfig}
                      currentScore={score}
                    />
                  )}
                </div>
              )}

              {/* Reset Button */}
              {!showFeedback && (
                <div className="flex justify-center">
                  <button
                    onClick={resetGame}
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-gray-300 to-slate-300 hover:from-gray-400 hover:to-slate-400 text-gray-700 px-4 sm:px-5 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-base shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 border-2 border-gray-400"
                  >
                    <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
                    Reiniciar
                  </button>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Progress Modal - Available in all phases */}
      <ProgressModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        onResetProgress={resetGame}
        stats={accumulatedStats || {
          score,
          attempts,
          accuracy,
          streak,
          bestStreak,
          totalQuestions,
          correctAnswers,
          incorrectAnswers,
          regionStats
        }}
        achievements={accumulatedStats && accumulatedAchievements ? accumulatedAchievements : achievements}
        isLoading={isLoadingStats}
        isAccumulated={!!accumulatedStats}
      />

      {/* Play Mode Modal (Quiz / Examen) */}
      {showPlayModal && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 text-center">¿Qué quieres jugar?</h3>
            <p className="text-sm text-gray-600 mb-4 text-center">
              Elige si deseas practicar con el modo Quiz libre o realizar un Examen cronometrado.
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => {
                  setShowPlayModal(false);
                  setGamePhase('setup');
                }}
                className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
              >
                Quiz de práctica
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPlayModal(false);
                  setGamePhase('exam-setup');
                }}
                className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
              >
                Examen de evaluación
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowPlayModal(false)}
              className="mt-4 w-full px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <LogOut className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">¿Salir del Quiz?</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Tu progreso se guardará automáticamente. ¿Estás seguro de que quieres salir al ranking?
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-xl font-bold transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={exitToLobby}
                className="flex-1 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
              >
                Salir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Achievement Notification */}
      <AchievementNotification
        achievement={newAchievement}
        onClose={() => setNewAchievement(null)}
      />

      {/* Advanced Filters Modal */}
      <AdvancedFilters
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        selectedDifficulties={selectedDifficulties}
        setSelectedDifficulties={setSelectedDifficulties}
        selectedTypes={selectedTypes}
        setSelectedTypes={setSelectedTypes}
      />

      {/* Save Status Indicator */}
      <SaveIndicator status={saveStatus} message={saveMessage} />

      {/* Version Panel - shown once per version */}
      {showVersionPanel && appVersion && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative max-w-lg w-full bg-white/95 rounded-3xl shadow-2xl border border-purple-100 animate-fade-in">
            <div className="absolute -top-5 right-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[0.65rem] sm:text-xs font-semibold px-3 py-1 rounded-full shadow-lg uppercase tracking-[0.25em]">
              Nueva versión
            </div>

            <div className="p-5 sm:p-6 md:p-7 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl shadow-lg border border-purple-100">
                    <Award className="w-6 h-6 sm:w-7 sm:h-7 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-purple-400 font-semibold">Actualización disponible</p>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 leading-snug">
                      Versión {appVersion}
                    </h3>
                  </div>
                </div>

                <button
                  onClick={() => setShowVersionPanel(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors text-xs sm:text-sm font-semibold"
                >
                  Cerrar
                </button>
              </div>

              <div className="rounded-2xl bg-gradient-to-br from-purple-50 via-white to-pink-50 border border-purple-100 p-4 sm:p-5 max-h-64 overflow-y-auto">
                <h4 className="text-xs sm:text-sm font-semibold text-gray-800 mb-2 sm:mb-3">
                  Cambios en esta versión
                </h4>
                {appChanges.length === 0 ? (
                  <p className="text-xs sm:text-sm text-gray-500">
                    Esta versión incluye mejoras internas y correcciones menores.
                  </p>
                ) : (
                  <ul className="space-y-1.5 text-xs sm:text-sm text-gray-700 list-disc list-inside">
                    {appChanges.map((change, index) => (
                      <li key={index}>{change}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-1">
                <p className="text-[0.7rem] sm:text-xs text-gray-500 text-center sm:text-left">
                  Este panel solo se mostrará la primera vez que entres con cada nueva versión de la aplicación.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <button
                    onClick={() => alert('Próximamente podrás recibir notificaciones automáticas de nuevas versiones.')}
                    className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl border border-purple-300 text-purple-700 bg-white hover:bg-purple-50 text-xs sm:text-sm font-semibold shadow-sm hover:shadow-md transition-all"
                  >
                    Recibir notificación de nuevas actualizaciones
                  </button>
                  <button
                    onClick={() => setShowVersionPanel(false)}
                    className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-xs sm:text-sm font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    Entendido
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navbar (solo cuando el usuario está autenticado y no está en quiz/examen) */}
      {isAuthenticated && !['setup', 'playing', 'exam-setup', 'exam-playing'].includes(gamePhase) && (
        <div
          className={`fixed bottom-0 inset-x-0 md:hidden pointer-events-none ${
            showSocialTutorial && isMobile && (socialTutorialStep === 0 || socialTutorialStep === 1)
              ? 'z-[80]'
              : 'z-[40]'
          }`}
        >
              <div className="max-w-6xl mx-auto px-2 pb-2 pointer-events-auto">
            <div
              ref={bottomNavbarRef}
              className={`bg-white/95 border border-purple-100 shadow-xl rounded-2xl flex items-center px-2 py-1.5 gap-1.5 ${showSocialTutorial && isMobile && (socialTutorialStep === 0 || socialTutorialStep === 1) ? 'relative z-[70]' : ''}`}
            >
              <button
                type="button"
                onClick={() => setGamePhase('lobby')}
                className={`flex-1 flex flex-col items-center justify-center px-2 py-1 rounded-xl text-[0.65rem] font-semibold transition-all ${
                  gamePhase === 'lobby'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-purple-50'
                }`}
              >
                <span>Lobby</span>
              </button>

              <button
                type="button"
                onClick={() => setShowPlayModal(true)}
                className={`flex-[1.2] flex flex-col items-center justify-center px-3 py-1.5 rounded-2xl text-xs font-bold transition-all ${
                  showPlayModal
                    ? 'bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 text-white shadow-lg'
                    : 'bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 text-white shadow-md hover:shadow-lg'
                }`}
              >
                <span>Jugar</span>
              </button>

              <button
                type="button"
                onClick={() => setGamePhase('social')}
                className={`flex-1 flex flex-col items-center justify-center px-2 py-1 rounded-xl text-[0.65rem] font-semibold transition-all ${
                  gamePhase === 'social'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-purple-50'
                }`}
              >
                <span>Social</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Panel - Press Ctrl+Shift+D to open */}
      <DebugPanel />
    </div>
  );
}

export default App;
